import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { readAuthRedirectError } from '../lib/auth-callback-url.js'
import { ensureOnboardedAndLogin } from '../lib/auth-context.js'
import { AUTH_CALLBACK_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { loadPendingGoogleLink, recordGoogleLinkError } from '../lib/google-link-pending.js'
import { supabase } from '../lib/supabase.js'

/**
 * OAuth コールバック画面。Supabase が URL ハッシュからセッションを取り出すのを待ち、
 * 完了したら /auth/signup → /auth/login を呼んでオンボーディングしてからホームへ。
 *
 * 設定画面からの Google アカウントの紐付け（linkIdentity）もここに戻ってくる。
 * その場合はホームではなく設定画面へ戻し、後始末（古い紐付けを外す等）は設定画面で行う。
 * 紐付けが失敗して戻ってきたときも、ログイン済みのセッションはそのまま残っている。
 */
export function AuthCallbackPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // Supabase が URL を片付ける前に、失敗の理由を読んでおく
    const linkError = readAuthRedirectError(window.location.search, window.location.hash)
    const isGoogleLink = loadPendingGoogleLink() !== null
    if (isGoogleLink && linkError) recordGoogleLinkError(linkError)
    finishSignIn()
      .then(() => {
        if (!cancelled) navigate(isGoogleLink ? '/settings' : '/', { replace: true })
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(toUserMessage(err, AUTH_CALLBACK_ERROR_FALLBACK))
      })
    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center">
      {error ? (
        <div className="max-w-sm text-center space-y-4 px-6">
          <p className="text-red-600 font-medium text-lg">ログインを完了できませんでした</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium hover:opacity-90 transition-opacity"
          >
            ログイン画面に戻る
          </button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">ログイン中…</p>
      )}
    </div>
  )
}

/** Supabase が URL からセッションを取り出すのを待ち、オンボーディングとログイン記録を済ませる */
async function finishSignIn(): Promise<void> {
  if (!(await currentSession())) {
    // 一度だけ待って再試行（Supabase が hash を解析する間）
    await new Promise((r) => setTimeout(r, SESSION_RETRY_DELAY_MS))
    if (!(await currentSession())) throw new Error('セッションを確立できませんでした')
  }
  await ensureOnboardedAndLogin()
}

const SESSION_RETRY_DELAY_MS = 200

async function currentSession() {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session
}
